import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useState } from "react";
import { NumberCard } from "../../../../components/cards/numberCard";
import { Icon } from "../../../../components/icon/icon";
import { AddPayoutAccountModal } from "../../../../components/modal/app_modals/addPayoutAccountModal";
import { ConfirmDeleteModal } from "../../../../components/modal/app_modals/confirmDeleteModal";
import { EventTicketModal } from "../../../../components/modal/app_modals/eventTicketModal";
import { ViewPayoutModal } from "../../../../components/modal/app_modals/viewPayoutModal";
import { Pill } from "../../../../components/pill";
import { TableComponent } from "../../../../components/table";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useAddEventTicketMutation,
  useDeleteEventTicketMutation,
  useEditEventTicketMutation,
  useGetEventTicketsBreakdownQuery,
} from "../../../../redux/features/event/event.api";
import {
  CreatedEvent,
  CreateTicket,
  GetEventTicketsBreakdownResponse,
  GetEventTicketsResponse,
} from "../../../../redux/features/event/event.types";
import {
  useGetPayoutAccountQuery,
  useGetPayoutHistoryQuery,
  useGetPurchaseStatisticsQuery,
} from "../../../../redux/features/payment/payment.api";
import {
  PayoutCurrency,
  SinglePayout,
} from "../../../../redux/features/payment/payment.types";
import {
  currencyShorts,
  paidTooltip,
  pendingPayoutTooltip,
} from "../../../../utils/constants";
import {
  deepCopy,
  formatDate,
  formatToCurrency,
  getCurrencyShort,
  moveElement,
} from "../../../../utils/helpers";
import { EventPermissions, QueryParams } from "../../../../utils/types";

const SingleTicket = ({
  ticket,
  ticketPurchaseBreakdown,
  setSelectedTicketIdx,
  setSelectedTicket,
  setSelectedTicketId,
  handleModalOpen,
  idx,
  userEventPermissions,
}: {
  ticket: CreateTicket & {
    _id: string;
    quantity: number;
  };
  ticketPurchaseBreakdown?: GetEventTicketsBreakdownResponse;
  setSelectedTicketIdx: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  setSelectedTicket: React.Dispatch<
    React.SetStateAction<CreateTicket | undefined>
  >;
  setSelectedTicketId: React.Dispatch<React.SetStateAction<string | undefined>>;
  handleModalOpen: (name: string) => void;
  idx: number;
  userEventPermissions: EventPermissions[];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `${ticket._id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      key={`${ticket._id}`}
      className="flex items-center gap-4"
      ref={setNodeRef}
      style={style}
    >
      {userEventPermissions.includes("editTicketDetails") && (
        <button {...attributes} {...listeners} className="w-fit" type="button">
          <Icon svg="grip" width={20} height={20} className="cursor-grab" />
        </button>
      )}

      <div className="flex-1 text-sm text-grey-900">
        <p className="text-base font-bold">{ticket.name}</p>
        <p className="capitalize">
          {ticket.ticketType === "free"
            ? "Free"
            : formatToCurrency(ticket.price!, {
                currency: getCurrencyShort(ticket.currency ?? "NGN"),
              })}
        </p>
        <p className="">
          {ticket.quantityType === "unlimited"
            ? `${ticketPurchaseBreakdown?.data?.find((tpb) => tpb.ticketId === ticket._id)?.totalQuantityPurchased ?? 0} sold • Unlimited`
            : `${ticketPurchaseBreakdown?.data?.find((tpb) => tpb.ticketId === ticket._id)?.totalQuantityPurchased ?? 0}/${ticket.quantity} registered`}
        </p>
      </div>

      {userEventPermissions.includes("editTicketDetails") && (
        <button
          className=""
          type="button"
          onClick={() => {
            setSelectedTicketIdx(idx);
            setSelectedTicket(ticket);
            setSelectedTicketId(ticket._id);
            handleModalOpen("eventTicketModal");
          }}
        >
          <Icon svg="pencil" width={20} height={20} />
        </button>
      )}

      {userEventPermissions.includes("deleteTicket") && (
        <button
          className=""
          type="button"
          onClick={() => {
            setSelectedTicketIdx(idx);
            setSelectedTicket(ticket);
            setSelectedTicketId(ticket._id);
            handleModalOpen("confirmDeleteModal");
          }}
        >
          <Icon svg="bin" width={20} height={20} />
        </button>
      )}
    </div>
  );
};

export const EventTickets = ({
  event,
  data,
  fetchingTickets,
  refetchTickets,
  hasPaidTickets = false,
  eventCurrency,
  userEventPermissions,
}: {
  event: CreatedEvent;
  data?: GetEventTicketsResponse;
  fetchingTickets?: boolean;
  refetchTickets: () => void;
  hasPaidTickets?: boolean;
  eventCurrency: PayoutCurrency;
  userEventPermissions: EventPermissions[];
}) => {
  const [selectedTicket, setSelectedTicket] = useState<CreateTicket>();
  const [selectedTicketIdx, setSelectedTicketIdx] = useState<number>();
  const [selectedTicketId, setSelectedTicketId] = useState<string>();
  const [eventTickets, setEventTickets] = useState<
    (CreateTicket & { _id: string; quantity: number })[]
  >([]);
  const [selectedPayout, setSelectedPayout] = useState<SinglePayout>();
  const [payoutParams, setPayoutParams] = useState<QueryParams>({
    page: 1,
    limit: 10,
  });

  const { handleModalClose, handleModalOpen, modal, modalOpen } =
    useModalControl();

  const closeTicketModal = () => {
    handleModalClose();
    setSelectedTicket(undefined);
    setSelectedTicketId(undefined);
    setSelectedTicketIdx(undefined);
  };

  const { data: ticketPurchaseBreakdown } = useGetEventTicketsBreakdownQuery(
    {
      id: event._id,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: payoutHistory, isFetching: fetchingPayoutHistory } =
    useGetPayoutHistoryQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: statistics, isFetching: fetchingStatistics } =
    useGetPurchaseStatisticsQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true, skip: !hasPaidTickets },
    );

  const { data: payoutAccount } = useGetPayoutAccountQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [addTicketToEvent, { isLoading: addingTicket }] =
    useAddEventTicketMutation();

  const [editTicket, { isLoading: editingTicket }] =
    useEditEventTicketMutation();

  const [deleteTicket, { isLoading: deletingTicket }] =
    useDeleteEventTicketMutation();

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;

    if (over && active.id !== over.id) {
      const ticketsCopy = deepCopy(eventTickets);
      const ticketCopyMod = ticketsCopy.map((t) => {
        return {
          id: `${t._id}`,
          ...t,
        };
      });

      const oldIndex = ticketCopyMod.findIndex((item) => item.id === active.id);
      const newIndex = ticketCopyMod.findIndex((item) => item.id === over.id);

      setEventTickets(moveElement(ticketsCopy, oldIndex, newIndex));
    }
  };

  useEffect(() => {
    if (data?.data) {
      setEventTickets(
        data.data.tickets.map((t) => {
          return {
            ...t,
          };
        }),
      );
    }
  }, [data]);

  return (
    <>
      <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
        <div className="flex items-center justify-between gap-4">
          <MiniHeader header="Tickets" />

          {userEventPermissions.includes("addTicket") && (
            <button
              type="button"
              className="text-sm font-bold text-grey-700 underline underline-offset-2 md:text-base"
              onClick={() => {
                setSelectedTicketIdx(undefined);
                setSelectedTicket(undefined);
                setSelectedTicketId(undefined);
                handleModalOpen("eventTicketModal");
              }}
            >
              Add Ticket
            </button>
          )}
        </div>

        {hasPaidTickets && (
          <div className="grid grid-cols-1 gap-[10px] md:grid-cols-2">
            <NumberCard
              value={statistics?.data.totalAmount ?? 0}
              text="Total Tickets Sold"
              loading={fetchingStatistics}
              className="!hidden"
              currency={eventCurrency}
            />

            <NumberCard
              value={statistics?.data.totalSuccessfulPayments ?? 0}
              text="Total Amount Paid"
              tooltipId={paidTooltip.id}
              tooltipText={paidTooltip.text}
              loading={fetchingStatistics}
              currency={eventCurrency}
            />

            <NumberCard
              value={statistics?.data.totalPendingPayments ?? 0}
              text="Pending Payouts"
              tooltipId={pendingPayoutTooltip.id}
              tooltipText={pendingPayoutTooltip.text}
              loading={fetchingStatistics}
              currency={eventCurrency}
            />
          </div>
        )}

        <DndContext
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={eventTickets.map((t) => {
              return {
                id: `${t._id}`,
                ...t,
              };
            })}
          >
            <TableComponent
              headers={[]}
              rows={eventTickets.map((ticket, idx) => {
                return {
                  id: idx,
                  items: [
                    <SingleTicket
                      key={`${ticket._id}`}
                      ticket={ticket}
                      ticketPurchaseBreakdown={ticketPurchaseBreakdown}
                      setSelectedTicketIdx={setSelectedTicketIdx}
                      setSelectedTicketId={setSelectedTicketId}
                      setSelectedTicket={setSelectedTicket}
                      handleModalOpen={handleModalOpen}
                      idx={idx}
                      userEventPermissions={userEventPermissions.filter(
                        (perm) => {
                          const quantityPurchased =
                            ticketPurchaseBreakdown?.data?.find(
                              (tpb) => tpb.ticketId === ticket._id,
                            )?.totalQuantityPurchased ?? 0;

                          if (
                            eventTickets.length < 2 ||
                            quantityPurchased > 0
                          ) {
                            return perm !== "deleteTicket";
                          } else {
                            return true;
                          }
                        },
                      )}
                    />,
                  ],
                };
              })}
              isLoading={fetchingTickets}
              isRowClickable={false}
              isTableEmpty={eventTickets.length === 0}
              emptyTitle="No Tickets Created"
              emptySubTitle="When you add tickets they would show up here."
              emptyImage="/assets/images/ticket-glass.png"
              loadingRows={3}
              loadingHeight={63}
            />
          </SortableContext>
        </DndContext>
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <div className="mt-3 flex flex-col gap-3">
          <MiniHeader header="Payout History" />

          <TableComponent
            headers={[]}
            rows={
              payoutHistory?.data?.data?.map((payout, idx) => {
                return {
                  id: idx,
                  items: [
                    <div className="flex flex-wrap items-center gap-1 text-sm text-grey-600">
                      <p className="font-bold text-grey-900">
                        {formatToCurrency(payout.amount, {
                          currency: currencyShorts[eventCurrency],
                        })}
                      </p>
                      {payout.payoutAccount ? (
                        <p className="flex-1">
                          • {`${payout.ticketCount}`}{" "}
                          {payout.ticketCount === 1 ? "Ticket" : "Tickets"} Sold
                        </p>
                      ) : (
                        <p className="flex-1"></p>
                      )}
                      <div className="flex items-center gap-2">
                        <p className="">
                          {payout.status === "pending"
                            ? "To be paid on"
                            : "Paid on"}{" "}
                          {formatDate(
                            payout.status === "pending"
                              ? payout.payoutDate
                              : payout.payoutDate,
                            "ddd, MMMM DD, YYYY",
                          )}
                        </p>

                        <Pill
                          text={
                            payout.status === "pending" ? "PENDING" : "PAID"
                          }
                          variation={
                            payout.status === "pending" ? "yellow" : "green"
                          }
                        />
                      </div>
                    </div>,
                  ],
                };
              }) ?? []
            }
            onRowClick={(idx) => {
              setSelectedPayout(payoutHistory?.data?.data![idx]);
              handleModalOpen("viewPayoutModal");
            }}
            isRowClickable={true}
            isTableEmpty={[0, undefined].includes(
              payoutHistory?.data?.data?.length,
            )}
            emptyTitle="No Payouts Made Yet"
            emptySubTitle=" You get paid at the end of everyday once you’ve made ticket sales"
            emptyImage="/assets/images/bank-glass.png"
            ctaIcon="plus"
            ctaText={payoutAccount?.data ? undefined : "Add Bank Details"}
            onCTAClick={() => {
              handleModalOpen("addPayoutModal");
            }}
            isLoading={fetchingPayoutHistory}
            isPaginated={true}
            currentPage={payoutParams.page}
            totalCount={payoutHistory?.data?.totalCount ?? 0}
            itemsPerPage={payoutParams.limit}
            onPageChange={(page) => {
              setPayoutParams({
                ...payoutParams,
                page: page,
              });
            }}
          />
        </div>
      </BgWrapper>

      <EventTicketModal
        open={modalOpen && modal === "eventTicketModal"}
        handleClose={() => {
          closeTicketModal();
        }}
        ticket={selectedTicket}
        lockCurrency={eventCurrency}
        selectedIdx={selectedTicketIdx}
        tickets={eventTickets}
        onSuccess={async (ticket, resetForm) => {
          if (selectedTicketId !== undefined) {
            const resp = await editTicket({
              id: selectedTicketId,
              payload: { ...(ticket as CreateTicket) },
            });

            if (resp.data?.status === "success") {
              closeTicketModal();
              refetchTickets();
              resetForm();
            }
          } else {
            const resp = await addTicketToEvent({
              id: event._id,
              payload: { ...(ticket as CreateTicket) },
            });

            if (resp.data?.status === "success") {
              closeTicketModal();
              refetchTickets();
              resetForm();
            }
          }
        }}
        returnMode="singleValue"
        loading={addingTicket || editingTicket}
        quantityPurchased={
          ticketPurchaseBreakdown?.data?.find(
            (tpb) => tpb.ticketId === selectedTicketId,
          )?.totalQuantityPurchased
        }
        isPublishedEventTicket
      />

      <ConfirmDeleteModal
        open={
          selectedTicketId !== undefined &&
          modalOpen &&
          modal === "confirmDeleteModal"
        }
        handleClose={() => {
          closeTicketModal();
        }}
        title={`You're about to delete "${selectedTicket?.name}"?`}
        subtitle="Deleting this ticket would remove it from the event page and guest would no longer be able to purchase this ticket. However, tickets already bought by guests would not be affected."
        onProceed={async () => {
          const resp = await deleteTicket({
            id: selectedTicketId!,
          });

          if (resp.data?.status === "success") {
            closeTicketModal();
            refetchTickets();
          }
        }}
        loading={deletingTicket}
      />

      <AddPayoutAccountModal
        open={modalOpen && modal === "addPayoutModal"}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={() => {
          handleModalClose();
        }}
        mode="create"
        account={undefined}
      />

      {selectedPayout && (
        <ViewPayoutModal
          open={modalOpen && modal === "viewPayoutModal"}
          handleClose={() => {
            handleModalClose();
            setSelectedPayout(undefined);
          }}
          payout={selectedPayout}
          eventId={event?._id ?? ""}
          currency={eventCurrency}
        />
      )}
    </>
  );
};
